import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import MarkdownRenderer from "components/MarkdownRenderer";
import ThinkingAnimation from "components/ThinkingAnimation";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { GRADIENTS } from "utils/designTokens";

/**
 * InlineBriefing
 *
 * Renders an auto-triggered AI summary inline on the page — no button, no modal.
 * The briefing fires on mount and re-fires when `endpoint` or `params` change.
 *
 * Used by:
 *   - Press Digest v2  (news catchup summary)
 *   - Digital Presence v2  (competitor quick catchup)
 *   - KOL Insights  (KOL monthly briefing)   [M004]
 *
 * Props:
 *   briefing   — the markdown string to render (from useInlineBriefingData)
 *   loading    — boolean
 *   error      — error object or null
 *   title      — optional heading above the markdown
 *   minHeight  — minimum card height while loading (default 180px)
 */
function InlineBriefing({ briefing, loading, error, title, minHeight }) {

  const isPlaceholder = loading || error || !briefing;

  return (
    <GlassCard
      sx={{
        minHeight,
        "&:hover": { transform: "none", boxShadow: "none" },
      }}
    >
      <VuiBox sx={{ position: "relative", zIndex: 1 }}>

        {/* Optional title with gradient accent */}
        {title && (
          <VuiBox display="flex" alignItems="center" mb={2}>
            <VuiBox
              sx={{
                width: 3,
                height: 18,
                borderRadius: "2px",
                background: GRADIENTS.accent,
                mr: 1.5,
                flexShrink: 0,
              }}
            />
            <VuiTypography
              variant="h6"
              color="white"
              fontWeight="medium"
              sx={{ fontSize: "0.95rem", letterSpacing: "0.01em" }}
            >
              {title}
            </VuiTypography>
          </VuiBox>
        )}

        {/* Content area */}
        {loading ? (
          <ThinkingAnimation
            size="sm"
            message="Generating briefing…"
            containerHeight={minHeight - 60}
          />
        ) : error ? (
          <StandardMessageDisplay
            type="error"
            title="Could not load briefing"
            message="Please try refreshing the page."
            containerHeight={minHeight - 60}
          />
        ) : !briefing ? (
          <StandardMessageDisplay
            type="noData"
            title="No briefing available"
            message="No content was found for the selected period."
            containerHeight={minHeight - 60}
          />
        ) : (
          <MarkdownRenderer markdownText={briefing} />
        )}

      </VuiBox>
    </GlassCard>
  );
}

InlineBriefing.propTypes = {
  briefing: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.any,
  title: PropTypes.string,
  minHeight: PropTypes.number,
};

InlineBriefing.defaultProps = {
  briefing: null,
  loading: false,
  error: null,
  title: null,
  minHeight: 180,
};

export default InlineBriefing;
